import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Todo from "./Todo";
import Cookies from "js-cookie";
import { v4 as uuid } from "uuid";
import useRoomMedia from "./hooks/useRoomMedia";
import useRoomTodos from "./hooks/useRoomTodos";
import useRoomWebRTC from "./hooks/useRoomWebRTC";
import useRoomSession from "./hooks/useRoomSession";
import SyncedCountdown from "./SyncedCountdown";
import { createRoomDocument, getRoom, leaveRoom } from "./firebase";
import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4";
import confetti from "./confetti";

const VIDEO_IDS = {
  lofi: "rFZHOHl-L8A",
  focus: "1ZYbU82GVz4",
  white: "nMfPqeZjc2c",
  forest: "IvjMgVS6kng",
  silent: "",
};

const RoomPage = ({ player }) => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [copied, setCopied] = useState(false);
  const [shouldInitiate, setShouldInitiate] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [roomState, setRoomState] = useState("checking"); // checking | ready | expired | error
  const [roomError, setRoomError] = useState(null);
  const [cameraMessage, setCameraMessage] = useState("");
  const [showPlayPrompt, setShowPlayPrompt] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const participantId = useMemo(() => uuid(), []);
  const displayName = useMemo(
    () => Cookies.get("username") || "Guest",
    []
  );
  const {
    localStream,
    startMedia,
    permissionState,
    error: mediaError,
  } = useRoomMedia();
  const {
    localTodos,
    setLocalTodos,
    peerTodos,
  } = useRoomTodos({
    roomId: roomState === "ready" ? roomId : null,
    participantId,
    displayName,
  });
  const { remoteStream, connectionState, iceConnectionState } = useRoomWebRTC({
    roomId: roomState === "ready" ? roomId : null,
    participantId,
    localStream,
    shouldInitiate: roomState === "ready" && shouldInitiate,
    onError: setConnectionError,
  });

  // Track if this user is the room creator
  const [creatorId, setCreatorId] = useState(null);
  const isCreator = useMemo(
    () => creatorId === participantId,
    [creatorId, participantId]
  );

  // Session management
  const {
    sessionState,
    startSession,
    completeSession,
    togglePause,
    restartSession,
  } = useRoomSession({
    roomId: roomState === "ready" ? roomId : null,
    participantId,
    isCreator,
  });

  const shareLink = useMemo(() => {
    if (!roomId) return "";
    return `${window.location.origin}/room/${roomId}`;
  }, [roomId]);

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard?.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error("Could not copy room link", error);
      setCopied(false);
    }
  };

  useEffect(() => {
    if (!roomId) return;
    mixpanel.track("Room visit", { roomId });
    ReactGA.event({
      category: "Room",
      action: "Room visit",
      label: roomId,
    });
  }, [roomId]);

  useEffect(() => {
    if (!mediaError) return;
    mixpanel.track("Room camera error", { roomId, message: mediaError.message });
  }, [mediaError, roomId]);

  const handleLeave = useCallback(async () => {
    setLeaving(true);
    try {
      await leaveRoom({ roomId, participantId });
    } catch (error) {
      console.error("Failed to leave room", error);
    } finally {
      navigate("/");
    }
  }, [navigate, participantId, roomId]);

  useEffect(() => {
    if (!roomId || !participantId) return;
    let active = true;
    const bootstrapRoom = async () => {
      try {
        const snapshot = await getRoom(roomId);
        if (snapshot.exists()) {
          const data = snapshot.data() || {};
          const expiresAtValue =
            data.expiresAt?.toMillis?.() || data.expiresAt || null;
          if (expiresAtValue && expiresAtValue < Date.now()) {
            if (!active) return;
            setRoomState("expired");
            setRoomError(
              "This room has expired. Create a new room to continue."
            );
            return;
          }
          if (active) {
            setShouldInitiate(!data?.creatorId || data.creatorId === participantId);
            setCreatorId(data.creatorId || participantId);
            setRoomState("ready");
          }
          return;
        }

        await createRoomDocument({
          roomId,
          creatorName: displayName,
          creatorId: participantId,
        });
        if (!active) return;
        setShouldInitiate(true);
        setCreatorId(participantId);
        setRoomState("ready");
      } catch (error) {
        console.error("Failed to bootstrap room", error);
        if (!active) return;
        setRoomState("error");
        setRoomError("Could not join this room. Try again or create a new one.");
      }
    };
    bootstrapRoom();
    return () => {
      active = false;
    };
  }, [displayName, participantId, roomId]);

  const requestCamera = useCallback(async () => {
    setCameraMessage("Requesting camera...");
    console.info("[room] requestCamera start");
    try {
      await startMedia();
      setCameraMessage("Camera started");
      console.info("[room] camera started");
    } catch (err) {
      setCameraMessage(err?.message || "Unable to start camera");
      console.error("[room] requestCamera error", err);
    }
  }, [startMedia]);

  useEffect(() => {
    if (roomState !== "ready") return;
    requestCamera();
  }, [requestCamera, roomState]);

  useEffect(() => {
    if (roomState !== "ready") return;
    if (!shouldInitiate && (!peerTodos || peerTodos.length === 0)) {
      setShouldInitiate(true);
    }
  }, [peerTodos, roomState, shouldInitiate]);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      // Guard to prevent re-attachment if stream is already set
      if (localVideoRef.current.srcObject === localStream) {
        return;
      }

      const videoTracks = localStream.getVideoTracks();

      if (videoTracks.length === 0) {
        console.error("[room] No video tracks in stream!");
        return;
      }

      localVideoRef.current.srcObject = localStream;

      // Ensure video plays
      localVideoRef.current.play().catch((err) => {
        console.warn("[room] local video play failed:", err);
      });
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      // Guard to prevent re-attachment if stream is already set
      if (remoteVideoRef.current.srcObject === remoteStream) {
        return;
      }

      remoteVideoRef.current.srcObject = remoteStream;
      // Ensure video plays
      remoteVideoRef.current.play().catch((err) => {
        console.warn("[room] remote video play failed:", err);
      });
    }
  }, [remoteStream]);

  useEffect(() => {
    return () => {
      if (!roomId || !participantId) return;
      leaveRoom({ roomId, participantId }).catch(() => {});
    };
  }, [participantId, roomId]);

  // Handle music playback based on session state
  useEffect(() => {
    if (!player) {
      console.warn("[Room] Player not available");
      return;
    }

    const { status, mode, paused } = sessionState;

    const playMusic = async () => {
      if (status === "running" && !paused && mode !== "silent") {
        const videoId = VIDEO_IDS[mode] || VIDEO_IDS.lofi;
        if (videoId) {
          try {
            console.info("[Room] Attempting to start music:", mode, videoId);
            
            // Check if player is ready
            let playerReady = false;
            if (player.getPlayerState && typeof player.getPlayerState === 'function') {
              try {
                const state = player.getPlayerState();
                console.info("[Room] Player state:", state);
                playerReady = state !== undefined && state !== -1;
              } catch (e) {
                console.warn("[Room] Could not get player state, assuming ready");
                playerReady = true;
              }
            } else {
              // If getPlayerState doesn't exist, assume player is ready
              playerReady = true;
            }
            
            if (!playerReady) {
              console.warn("[Room] Player not ready, will retry on user interaction");
              return;
            }
            
            // Cue the video first
            if (player.cueVideoById && typeof player.cueVideoById === 'function') {
              console.info("[Room] Cueing video");
              player.cueVideoById(videoId);
              
              // Then explicitly play after a delay
              setTimeout(() => {
                if (player.playVideo && typeof player.playVideo === 'function') {
                  console.info("[Room] Calling playVideo()");
                  player.playVideo();
                  
                  // Check if it's actually playing
                  setTimeout(() => {
                    if (player.getPlayerState && typeof player.getPlayerState === 'function') {
                      const finalState = player.getPlayerState();
                      console.info("[Room] Final player state:", finalState, finalState === 1 ? "PLAYING ✓" : "NOT PLAYING ✗");
                      
                      // If not playing and not silent mode, show prompt
                      if (finalState !== 1 && mode !== "silent") {
                        console.warn("[Room] Autoplay blocked - showing user prompt");
                        setShowPlayPrompt(true);
                      } else if (finalState === 1) {
                        setShowPlayPrompt(false);
                      }
                    }
                  }, 500);
                }
              }, 1000);
            } else if (player.loadVideoById && typeof player.loadVideoById === 'function') {
              console.info("[Room] Using loadVideoById (fallback)");
              player.loadVideoById({
                videoId: videoId,
                startSeconds: 0
              });
              
              // Also call play explicitly
              setTimeout(() => {
                if (player.playVideo && typeof player.playVideo === 'function') {
                  console.info("[Room] Calling playVideo() after loadVideoById");
                  player.playVideo();
                  
                  // Check result
                  setTimeout(() => {
                    if (player.getPlayerState && typeof player.getPlayerState === 'function') {
                      const finalState = player.getPlayerState();
                      if (finalState !== 1 && mode !== "silent") {
                        setShowPlayPrompt(true);
                      } else if (finalState === 1) {
                        setShowPlayPrompt(false);
                      }
                    }
                  }, 500);
                }
              }, 1000);
            }
            
            console.info("[Room] Music commands sent");
          } catch (err) {
            console.error("[Room] Music play error:", err);
          }
        }
      } else if (status === "running" && paused) {
        console.info("[Room] Pausing music");
        try {
          if (player.pauseVideo && typeof player.pauseVideo === 'function') {
            player.pauseVideo();
          }
        } catch (err) {
          console.error("[Room] Pause error:", err);
        }
      } else if (status !== "running") {
        console.info("[Room] Stopping music (status:", status, ")");
        try {
          if (player.stopVideo && typeof player.stopVideo === 'function') {
            player.stopVideo();
          } else if (player.pauseVideo && typeof player.pauseVideo === 'function') {
            player.pauseVideo();
          }
        } catch (err) {
          console.error("[Room] Stop error:", err);
        }
      }
    };

    playMusic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, sessionState.status, sessionState.paused, sessionState.mode]);

  const partner = peerTodos[0];
  const partnerTodos = partner?.todos || [];
  const partnerName = partner?.displayName || "Partner";

  // Determine connection status based on peer presence
  const hasPeer = peerTodos && peerTodos.length > 0;

  // Debug banner - set to true to show in development
  const showDebug = false;
  
  const debugBanner = showDebug ? (
    <div className="fixed top-3 left-1/2 z-50 w-[90vw] max-w-4xl -translate-x-1/2 rounded-xl border border-amber-500/40 bg-amber-500/15 px-4 py-2 text-[11px] text-amber-100 shadow-lg backdrop-blur">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold uppercase tracking-[0.2em] text-amber-200">
          debug
        </span>
        <span>roomState: {roomState}</span>
        <span>permission: {permissionState}</span>
        <span>localStream: {localStream ? "yes" : "no"}</span>
        <span>
          localTracks:{" "}
          {localStream
            ? localStream
                .getTracks()
                .map((t) => `${t.kind}:${t.readyState}`)
                .join("|")
            : "none"}
        </span>
        <span>connection: {connectionState}</span>
        <span>ice: {iceConnectionState}</span>
        <span>hasPeer: {hasPeer ? "yes" : "no"}</span>
        <span>shouldInitiate: {shouldInitiate ? "yes" : "no"}</span>
        <span>mediaError: {mediaError?.message || "none"}</span>
        {cameraMessage && <span>cameraMsg: {cameraMessage}</span>}
        {(!localStream || permissionState !== "granted") && (
          <button
            onClick={requestCamera}
            className="rounded-full border border-amber-300/50 bg-amber-500/20 px-3 py-1 text-[11px] font-semibold text-amber-100 shadow-sm transition hover:bg-amber-500/30"
          >
            Retry camera
          </button>
        )}
      </div>
    </div>
  ) : null;

  if (roomState !== "ready") {
    const headline =
      roomState === "expired"
        ? "Room expired"
        : roomState === "error"
        ? "Room unavailable"
        : "Loading...";

    return (
      <div className="lobby-container text-white">
        {/* Background layers */}
        <div className="lobby-bg-base" />
        <div className="lobby-noise" />
        <div className="lobby-vignette" />

        {debugBanner}

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--color-text-muted)]">
              Deep work room
            </p>
            <h1 className="mt-4 font-display text-3xl font-light lg:text-4xl">{headline}</h1>
            <p className="mt-3 text-[var(--color-text-secondary)]">
              {roomError ||
                "Preparing your room. If this takes longer than a few seconds, try again."}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="lobby-enter-btn !w-auto"
              >
                <span className="lobby-enter-text">Go Home</span>
                <span className="lobby-enter-arrow">→</span>
              </button>
              <button
                onClick={() => navigate("/")}
                className="lobby-room-btn"
              >
                Create new room
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render appropriate view based on session status
  const { status } = sessionState;
  
  if (status === "idle" && !isCreator) {
    return (
      <RoomWaitingView
        debugBanner={debugBanner}
        handleLeave={handleLeave}
        leaving={leaving}
        roomId={roomId}
        copyLink={copyLink}
        copied={copied}
        partnerName={partner?.displayName || "host"}
      />
    );
  }

  if (status === "idle" && isCreator) {
    return (
      <RoomSetupView
        debugBanner={debugBanner}
        handleLeave={handleLeave}
        leaving={leaving}
        roomId={roomId}
        copyLink={copyLink}
        copied={copied}
        onStartSession={startSession}
        localVideoRef={localVideoRef}
        remoteVideoRef={remoteVideoRef}
        mediaError={mediaError}
        permissionState={permissionState}
        requestCamera={requestCamera}
      />
    );
  }

  if (status === "complete") {
    return (
      <RoomCompleteView
        debugBanner={debugBanner}
        handleLeave={handleLeave}
        leaving={leaving}
        roomId={roomId}
        isCreator={isCreator}
        onRestart={restartSession}
        duration={sessionState.duration}
      />
    );
  }

  // status === "running"
  return (
    <RoomSessionView
      debugBanner={debugBanner}
      handleLeave={handleLeave}
      leaving={leaving}
      roomId={roomId}
      copyLink={copyLink}
      copied={copied}
      sessionState={sessionState}
      localVideoRef={localVideoRef}
      remoteVideoRef={remoteVideoRef}
      localTodos={localTodos}
      setLocalTodos={setLocalTodos}
      partnerTodos={partnerTodos}
      partnerName={partnerName}
      isCreator={isCreator}
      togglePause={togglePause}
      completeSession={completeSession}
      mediaError={mediaError}
      permissionState={permissionState}
      requestCamera={requestCamera}
      connectionError={connectionError}
      player={player}
      showPlayPrompt={showPlayPrompt}
      setShowPlayPrompt={setShowPlayPrompt}
    />
  );
};

// View Components
const RoomSetupView = ({
  debugBanner,
  handleLeave,
  leaving,
  roomId,
  copyLink,
  copied,
  onStartSession,
  localVideoRef,
  remoteVideoRef,
  mediaError,
  permissionState,
  requestCamera,
}) => {
  const [duration, setDuration] = useState(25);
  const [mode, setMode] = useState("lofi");

  const handleStart = () => {
    onStartSession({ duration, mode });
    mixpanel.track("Room session started", { duration, mode });
  };

  const musicOptions = [
    { id: "lofi", label: "Lo-fi", icon: "🎵" },
    { id: "focus", label: "Focus", icon: "🎯" },
    { id: "white", label: "White", icon: "🌊" },
    { id: "forest", label: "Forest", icon: "🌲" },
    { id: "silent", label: "Silent", icon: "🔇" },
  ];

  return (
    <div className="lobby-container text-white">
      {/* Background layers */}
      <div className="lobby-bg-base" />
      <div className="lobby-noise" />
      <div className="lobby-vignette" />

      {debugBanner}

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="lobby-room-btn"
          >
            {leaving ? "Leaving..." : "← Leave room"}
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]">
              <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
              Room: {roomId}
            </div>
            <button onClick={copyLink} className="lobby-room-btn">
              {copied ? "Copied!" : "Copy invite link"}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="mt-8 flex flex-1 flex-col lg:mt-12 lg:flex-row lg:gap-12">
          {/* Left: Header & Camera */}
          <div className="flex-1">
            <div className="lobby-overline">
              <span className="lobby-overline-line"></span>
              <span>Deep work room</span>
            </div>
            <h1 className="font-display text-4xl font-light lg:text-5xl">
              <span className="block">Configure your</span>
              <span className="block text-[var(--color-accent)] italic">session</span>
            </h1>
            <p className="mt-4 max-w-md text-[var(--color-text-secondary)]">
              Your partner will join and sync to your timer. Begin when ready.
            </p>

            {/* Camera preview */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
                <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
                  <span className="font-mono text-xs uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">You</span>
                  <span className="font-mono text-[10px] text-[var(--color-accent)]">Preview</span>
                </div>
                <div className="relative aspect-video bg-black">
                  <video
                    ref={localVideoRef}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scaleX(-1)" }}
                    autoPlay
                    playsInline
                    muted
                  />
                </div>
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {mediaError ? "Permission blocked" : "Camera active"}
                  </span>
                  {permissionState !== "granted" && (
                    <button
                      onClick={requestCamera}
                      className="font-mono text-[10px] text-[var(--color-accent)] hover:underline"
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-elevated)]">
                <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
                  <span className="font-mono text-xs uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">Partner</span>
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">Waiting</span>
                </div>
                <div className="relative aspect-video bg-black/50">
                  <video
                    ref={remoteVideoRef}
                    className="absolute inset-0 h-full w-full object-cover"
                    autoPlay
                    playsInline
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-xs text-[var(--color-text-muted)]">Partner will appear here</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Controls */}
          <div className="mt-8 w-full lg:mt-0 lg:max-w-sm">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
              {/* Duration */}
              <div className="lobby-section">
                <div className="lobby-field-header">
                  <span className="lobby-label">Duration</span>
                  <span className="lobby-value">{duration} min</span>
                </div>
                <input
                  type="range"
                  min="15"
                  max="90"
                  step="5"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="lobby-range"
                />
                <div className="lobby-range-labels">
                  <span>15m</span>
                  <span>90m</span>
                </div>
              </div>

              {/* Music mode */}
              <div className="lobby-section">
                <span className="lobby-label">Soundscape</span>
                <div className="lobby-music-grid mt-3">
                  {musicOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setMode(option.id)}
                      className={`lobby-music-btn ${mode === option.id ? "active" : ""}`}
                    >
                      <span className="lobby-music-icon">{option.icon}</span>
                      <span className="lobby-music-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Start button */}
              <div className="lobby-actions">
                <button onClick={handleStart} className="lobby-enter-btn">
                  <span className="lobby-enter-text">Begin Session</span>
                  <span className="lobby-enter-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoomWaitingView = ({
  debugBanner,
  handleLeave,
  leaving,
  roomId,
  copyLink,
  copied,
  partnerName,
}) => {
  return (
    <div className="lobby-container text-white">
      {/* Background layers */}
      <div className="lobby-bg-base" />
      <div className="lobby-noise" />
      <div className="lobby-vignette" />

      {debugBanner}

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="lobby-room-btn"
          >
            {leaving ? "Leaving..." : "← Leave room"}
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--color-accent)]"></span>
              Room: {roomId}
            </div>
            <button onClick={copyLink} className="lobby-room-btn">
              {copied ? "Copied!" : "Copy invite link"}
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="text-center">
            <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10">
              <svg
                className="h-10 w-10 animate-pulse text-[var(--color-accent)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--color-text-muted)]">
              Awaiting host
            </p>
            <h2 className="mt-4 font-display text-4xl font-light lg:text-5xl">
              <span className="block">Waiting for</span>
              <span className="block text-[var(--color-accent)] italic">{partnerName}</span>
            </h2>
            <p className="mx-auto mt-4 max-w-sm text-[var(--color-text-secondary)]">
              The session will begin when {partnerName} starts the timer. Take a breath.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoomSessionView = ({
  debugBanner,
  handleLeave,
  leaving,
  roomId,
  copyLink,
  copied,
  sessionState,
  localVideoRef,
  remoteVideoRef,
  localTodos,
  setLocalTodos,
  partnerTodos,
  partnerName,
  isCreator,
  togglePause,
  completeSession,
  mediaError,
  permissionState,
  requestCamera,
  connectionError,
  player,
  showPlayPrompt,
  setShowPlayPrompt,
}) => {
  const [volume, setVolume] = useState(70);
  const [musicStarted, setMusicStarted] = useState(false);
  const evening = useMemo(() => new Date().getHours() >= 18, []);

  useEffect(() => {
    if (player?.setVolume) {
      player.setVolume(volume);
    }
  }, [player, volume]);

  // Ensure music starts on user interaction (helps with autoplay restrictions)
  const ensureMusicPlaying = useCallback(() => {
    if (sessionState.status === "running" && !sessionState.paused && player) {
      const videoId = VIDEO_IDS[sessionState.mode] || VIDEO_IDS.lofi;
      if (videoId && sessionState.mode !== "silent") {
        console.info("[Room] User clicked - attempting to play music, videoId:", videoId);
        console.info("[Room] Player object:", {
          hasLoadVideoById: typeof player.loadVideoById === 'function',
          hasCueVideoById: typeof player.cueVideoById === 'function',
          hasPlayVideo: typeof player.playVideo === 'function',
          hasGetPlayerState: typeof player.getPlayerState === 'function',
        });
        
        try {
          // Try direct approach - just load and play with string ID
          if (player.loadVideoById && typeof player.loadVideoById === 'function') {
            console.info("[Room] Calling loadVideoById with string ID");
            // Try with just the string ID (simpler API)
            player.loadVideoById(videoId);
            
            setMusicStarted(true);
            setShowPlayPrompt(false);
            
            // Check if it worked
            setTimeout(() => {
              if (player.getPlayerState && typeof player.getPlayerState === 'function') {
                const finalState = player.getPlayerState();
                console.info("[Room] Final state after loadVideoById:", finalState);
                if (finalState === 1 || finalState === 3) {
                  console.info("[Room] ✓ Music is playing/loading!");
                } else {
                  console.warn("[Room] ✗ Music not playing, state:", finalState);
                  // Try one more time with playVideo
                  if (player.playVideo && typeof player.playVideo === 'function') {
                    console.info("[Room] Attempting playVideo() as backup");
                    player.playVideo();
                  }
                }
              }
            }, 1500);
            return;
          }
          
          // Fallback: cue then play
          if (player.cueVideoById && typeof player.cueVideoById === 'function') {
            console.info("[Room] Using cueVideoById fallback");
            player.cueVideoById(videoId);
            
            if (player.playVideo && typeof player.playVideo === 'function') {
              player.playVideo();
            }
            
            setMusicStarted(true);
            setShowPlayPrompt(false);
          }
        } catch (err) {
          console.error("[Room] Music play error:", err);
          setShowPlayPrompt(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicStarted, player, sessionState.status, sessionState.paused, sessionState.mode, setShowPlayPrompt]);

  useEffect(() => {
    // Reset music started flag when session ends
    if (sessionState.status !== "running") {
      setMusicStarted(false);
    }
  }, [sessionState.status]);

  return (
    <div
      className="relative min-h-screen text-white"
      onClick={ensureMusicPlaying}
    >
      {/* Background video */}
      <video
        src={
          evening
            ? "https://upcdn.io/FW25ant/raw/kaizen/man.mp4"
            : "https://upcdn.io/FW25ant/raw/kaizen/woman.mp4"
        }
        autoPlay
        loop
        playsInline
        muted
        className="fixed inset-0 h-full w-full object-cover"
      />
      <div className="fixed inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/80" />
      <div className="lobby-noise fixed inset-0" />

      {/* Play Music Prompt */}
      {showPlayPrompt && sessionState.mode !== "silent" && (
        <div className="fixed top-20 left-1/2 z-50 -translate-x-1/2 transform">
          <button
            onClick={(e) => {
              e.stopPropagation();
              ensureMusicPlaying();
            }}
            className="animate-pulse rounded border border-[var(--color-accent)] bg-[var(--color-accent)]/20 px-6 py-3 font-mono text-sm text-[var(--color-accent)] shadow-xl backdrop-blur transition hover:bg-[var(--color-accent)]/30"
          >
            Click to Play Music
          </button>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        {debugBanner}
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={handleLeave}
              disabled={leaving}
              className="lobby-room-btn backdrop-blur"
            >
              {leaving ? "Leaving..." : "← Leave room"}
            </button>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-black/50 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--color-text-tertiary)] backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
                Room: {roomId}
              </div>
              <button onClick={copyLink} className="lobby-room-btn backdrop-blur">
                {copied ? "Copied!" : "Copy invite link"}
              </button>
            </div>
          </div>

          {/* Timer */}
          <div className="flex flex-col items-center gap-6">
            <SyncedCountdown
              startedAt={sessionState.startedAt}
              duration={sessionState.duration}
              paused={sessionState.paused}
              onComplete={completeSession}
            />

            {/* Video panels */}
            <div className="w-full rounded-lg border border-[var(--color-border)] bg-black/50 p-4 backdrop-blur-sm">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-black/60">
                  <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
                    <span className="font-mono text-xs uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">You</span>
                    <span className="font-mono text-[10px] text-emerald-400">Live</span>
                  </div>
                  <div className="relative aspect-video bg-black">
                    <video
                      ref={localVideoRef}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ transform: "scaleX(-1)" }}
                      autoPlay
                      playsInline
                      muted
                    />
                  </div>
                  <div className="flex items-center justify-between px-4 py-2">
                    <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                      {mediaError ? "Permission blocked" : "Camera active"}
                    </span>
                    {permissionState !== "granted" && (
                      <button
                        onClick={requestCamera}
                        className="font-mono text-[10px] text-[var(--color-accent)] hover:underline"
                      >
                        Enable
                      </button>
                    )}
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-black/60">
                  <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
                    <span className="font-mono text-xs uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">{partnerName}</span>
                    <span className="font-mono text-[10px] text-emerald-400">Live</span>
                  </div>
                  <div className="relative aspect-video bg-black">
                    <video
                      ref={remoteVideoRef}
                      className="absolute inset-0 h-full w-full object-cover"
                      autoPlay
                      playsInline
                    />
                  </div>
                  {connectionError && (
                    <div className="border-t border-red-500/30 bg-red-500/10 px-4 py-2">
                      <span className="font-mono text-[10px] text-red-300">Connection issue</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tasks */}
            <div className="w-full rounded-lg border border-[var(--color-border)] bg-black/50 p-4 backdrop-blur-sm">
              <div className="grid gap-4 lg:grid-cols-2">
                <Todo
                  inline
                  title="Your tasks"
                  todos={localTodos}
                  setTodos={setLocalTodos}
                  disablePersistence
                  helperText="Focus stack - sync live to room"
                />
                <Todo
                  inline
                  readOnly
                  title={`${partnerName}'s tasks`}
                  todos={partnerTodos}
                  setTodos={() => {}}
                  disablePersistence
                  helperText="Partner's focus stack"
                />
              </div>
            </div>

            {/* Controls */}
            {isCreator && (
              <div className="flex items-center gap-4 rounded-full border border-[var(--color-border)] bg-black/60 px-6 py-3 backdrop-blur">
                <button
                  onClick={togglePause}
                  className="lobby-enter-btn !w-auto !px-6 !py-2"
                >
                  <span className="lobby-enter-text">{sessionState.paused ? "Resume" : "Pause"}</span>
                </button>
                <div className="h-6 w-px bg-[var(--color-border)]"></div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs uppercase tracking-[0.1em] text-[var(--color-text-muted)]">
                    Vol
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    className="lobby-range w-24"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RoomCompleteView = ({
  debugBanner,
  handleLeave,
  leaving,
  roomId,
  isCreator,
  onRestart,
  duration,
}) => {
  useEffect(() => {
    confetti.start();
    setTimeout(() => confetti.stop(), 9000);
    return () => confetti.stop();
  }, []);

  return (
    <div className="lobby-container text-white">
      {/* Background layers */}
      <div className="lobby-bg-base" />
      <div className="lobby-noise" />
      <div className="lobby-vignette" />

      {debugBanner}

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-8 lg:px-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="lobby-room-btn"
          >
            {leaving ? "Leaving..." : "← Leave room"}
          </button>
          <div className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--color-text-tertiary)]">
            <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
            Room: {roomId}
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--color-accent)]">
              Session complete
            </p>
            <h2 className="mt-4 font-display text-4xl font-light lg:text-6xl">
              <span className="block">Great</span>
              <span className="block text-[var(--color-accent)] italic">work.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-sm text-[var(--color-text-secondary)]">
              You completed a {duration}-minute deep work session together.
            </p>

            <div className="mt-8">
              {isCreator && (
                <button onClick={onRestart} className="lobby-enter-btn">
                  <span className="lobby-enter-text">Start New Session</span>
                  <span className="lobby-enter-arrow">→</span>
                </button>
              )}
              {!isCreator && (
                <p className="font-mono text-xs text-[var(--color-text-muted)]">
                  Waiting for host to start a new session...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomPage;


